import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { asStr, rowKey, diffCoords, CoordInput, TextInput } from './inline';

const LIMIT = 20;

const MISSING_FILTERS = [
  { key: 'missing', label: 'Missing coordinates' },
  { key: 'has', label: 'Has coordinates' },
  { key: 'all', label: 'All' },
];

// filter -> `missing` query param ('all' omits it)
const missingParam = (f) => (f === 'missing' ? true : f === 'has' ? false : undefined);

// Shared inline-edit + bulk-moderation page for hotels and restaurants.
// config: { title, singular, useList, useBulk }
export default function GeoModerationPage({ config }) {
  const { title, useList, useBulk } = config;
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [missing, setMissing] = useState('missing'); // backlog first
  const [edits, setEdits] = useState(() => new Map()); // rowKey -> { lat, lon, address }
  const [deletes, setDeletes] = useState(() => new Set()); // rowKeys marked for deletion
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error } = useList({
    page,
    limit: LIMIT,
    search,
    missing: missingParam(missing),
  });
  const rows = data?.data || [];
  const total = data?.total;

  const bulkM = useBulk({
    onSuccess: (res) => {
      const s = res?.data || {};
      toast.success(`Updated ${s.updated ?? 0} · deleted ${s.deleted ?? 0}${s.failed ? ` · ${s.failed} failed` : ''}`);
      setEdits(new Map());
      setDeletes(new Set());
      setConfirmOpen(false);
    },
    onError: (e) => { toast.error(e.message || 'Bulk save failed'); setConfirmOpen(false); },
  });

  const resetEdits = () => { setEdits(new Map()); setDeletes(new Set()); };
  const changeSearch = (v) => { setSearch(v); setPage(1); resetEdits(); };
  const changeMissing = (key) => { setMissing(key); setPage(1); resetEdits(); };
  const changePage = (p) => { setPage(p); resetEdits(); };

  const valFor = (row, field) => edits.get(rowKey(row))?.[field] ?? asStr(row[field]);
  const setField = (row, field, value) =>
    setEdits((prev) => {
      const key = rowKey(row);
      const next = new Map(prev);
      next.set(key, { ...(next.get(key) || {}), [field]: value });
      return next;
    });
  const toggleDelete = (row) =>
    setDeletes((prev) => {
      const key = rowKey(row);
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Assemble the bulk payload from every pending row change / deletion.
  const buildBatch = () => {
    const items = [];
    const invalidRows = [];
    for (const row of rows) {
      const key = rowKey(row);
      if (deletes.has(key)) { items.push({ id: row.id, delete: true }); continue; }
      const cur = { lat: valFor(row, 'lat'), lon: valFor(row, 'lon'), address: valFor(row, 'address') };
      const { changed, errors } = diffCoords(cur, row, { withAddress: true });
      if (errors.length) invalidRows.push(row.name || `#${row.id}`);
      else if (Object.keys(changed).length) items.push({ id: row.id, ...changed });
    }
    return { items, invalidRows };
  };

  const { items, invalidRows } = buildBatch();
  const deleteCount = items.filter((i) => i.delete).length;
  const updateCount = items.length - deleteCount;

  const submit = () => {
    if (invalidRows.length) {
      toast.error(`Fix out-of-range coordinates for: ${invalidRows.join(', ')}`);
      return;
    }
    if (!items.length) return;
    if (deleteCount > 0) { setConfirmOpen(true); return; }
    bulkM.mutate(items);
  };

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'name', header: 'Name', render: (r) => r.name || '—' },
    { key: 'city', header: 'City', render: (r) => r.city || '—' },
    {
      key: 'address',
      header: 'Address',
      render: (r) => (
        <TextInput
          value={valFor(r, 'address')}
          onChange={(v) => setField(r, 'address', v)}
          disabled={bulkM.isPending || deletes.has(rowKey(r))}
          placeholder="Street address"
        />
      ),
    },
    {
      key: 'lat',
      header: 'Lat',
      render: (r) => (
        <CoordInput
          value={valFor(r, 'lat')}
          onChange={(v) => setField(r, 'lat', v)}
          disabled={bulkM.isPending || deletes.has(rowKey(r))}
          lo={-90}
          hi={90}
        />
      ),
    },
    {
      key: 'lon',
      header: 'Lon',
      render: (r) => (
        <CoordInput
          value={valFor(r, 'lon')}
          onChange={(v) => setField(r, 'lon', v)}
          disabled={bulkM.isPending || deletes.has(rowKey(r))}
          lo={-180}
          hi={180}
        />
      ),
    },
    {
      key: '_del',
      header: 'Delete',
      render: (r) => (
        <input
          type="checkbox"
          checked={deletes.has(rowKey(r))}
          onChange={() => toggleDelete(r)}
          disabled={bulkM.isPending}
          aria-label={`Delete ${r.name || r.id}`}
        />
      ),
    },
  ];

  const saveBtn = (
    <button className="btn btn--primary" disabled={bulkM.isPending || items.length === 0} onClick={submit}>
      {bulkM.isPending ? 'Saving…' : `Save changes (${items.length})`}
    </button>
  );

  return (
    <div className="page">
      <div className="page__header"><h1>{title}</h1></div>

      <div className="page__toolbar page__toolbar--row">
        <SearchBar value={search} onSearch={changeSearch} placeholder="Search name, address, or city…" />
        <div className="seg">
          {MISSING_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`seg__btn${missing === f.key ? ' seg__btn--active' : ''}`}
              onClick={() => changeMissing(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText={`No ${title.toLowerCase()} found.`}
      />

      {data && total != null && (
        <Pagination page={page} limit={LIMIT} total={total} onPage={changePage} />
      )}

      {rows.length > 0 && <div className="page__footer-actions">{saveBtn}</div>}

      <ConfirmDialog
        open={confirmOpen}
        title="Apply bulk moderation?"
        message={`This will update ${updateCount} and permanently delete ${deleteCount} row(s).`}
        confirmLabel="Apply"
        pending={bulkM.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => bulkM.mutate(items)}
      />
    </div>
  );
}
